import type { OrderSummaryResponse } from '@thinkalpha/locates-ws-client/contracts/orders.js';
import type { Provider } from '@thinkalpha/ui-types/types/admin/lab';
import type { RevenueAnalyticsRow } from 'src/admin/LAB/RevenueAnalytics/types';
import { lapi as axios } from 'src/api';

export interface GetProviderRevenueAnalyticsOptions {
    /** Zero-based starting row. Omitted (with `limit`) when searching a symbol. */
    skip?: number;
    /** Page size. Omitted (with `skip`) when searching a symbol. */
    limit?: number;
    /**
     * Exact-match symbol filter. When set, the backend scopes the result
     * to that symbol and ignores `skip`/`limit`.
     */
    symbol?: string;
}

/**
 * Fetch one page of per-symbol revenue analytics for a single provider
 * and map the response onto the table's row shape. The provider name is
 * taken from the already-loaded provider rather than the response.
 *
 * Served by `GET /v1/orders/summary/by-symbol`; `providerId` scopes the
 * rows to the selected provider and `skip`/`limit` page them. `inventory`
 * is optional per row, so the SOD figures are left `null` when the backend
 * has no start-of-day data (the table renders those as a dash).
 */
export const getProviderRevenueAnalytics = async (
    provider: Provider,
    options: GetProviderRevenueAnalyticsOptions = {},
): Promise<RevenueAnalyticsRow[]> => {
    const { skip, limit, symbol } = options;
    const { data } = await axios.get<OrderSummaryResponse>('/v1/orders/summary/by-symbol', {
        params: { providerId: provider.id, skip, limit, symbol },
    });
    return data.rows.map((row) => ({
        providerName: provider.name,
        symbol: row.symbol,
        qty: row.quantity,
        avgPrice: row.averagePrice,
        totalCost: row.totalCost,
        curPrice: row.currentPrice,
        fairValue: row.fairValue,
        sodInventory: row.inventory?.startOfDay ?? null,
        remainingInventory: row.inventory?.remaining ?? null,
    }));
};
